import { createElement } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import html2canvas from "html2canvas";
import { fetchApprovedMrcBill } from "../../../components/ElectricityConsumerBill/fetchApprovedMrcBill";
import { mapApprovedMrcBillResponse } from "../../../components/ElectricityConsumerBill/mapApprovedMrcBillResponse";
import {
  computeMrcBillQrAmount,
  fetchMrcBillPaymentQr,
  parseMrcBillNumber,
} from "../../../components/ElectricityConsumerBill/fetchMrcBillPaymentQr";
import { getLogoColorLight } from "../../../components/ElectricityConsumerBill/discoBillBranding";
import MrcElectricityConsumerBill from "../../../components/ElectricityConsumerBill/MrcElectricityConsumerBill";
import { extractDiscoCodeFromSubDivisionCode } from "../../../utils/adminCompany";
import { createCPForm52OfMRCPdfDoc } from "./downloadCPForm52OfMRCPdf";
import {
  expandMrcBillMeterImages,
  extractMrcOracleTrackingId,
  formatCp52BillingMonth,
  mapMrcGetCp52ToFormData,
  unwrapMrcCp52Record,
} from "./mrcCp52Api";
import {
  enrichCp52BaseDataFromDocuments,
  resolveMrcTariffDisplay,
} from "./mrcReadingUtils";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const parseApplicationPayable = (row = {}) => {
  const text = String(
    row.payableWithinDueDate ??
      row.amountPayable ??
      row.payableAmount ??
      row.currentBillAmount ??
      row.billAmount ??
      ""
  )
    .replace(/[,\u066C\uFF0C\u201A\u02BC']/g, "")
    .replace(/[\u00A0\u202F\u2009\s]/g, "")
    .trim();
  const n = Number(text);
  return Number.isFinite(n) ? n : "";
};

const waitForImages = async (root, timeoutMs = 2500) => {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const imgs = Array.from(root.querySelectorAll("img"));
    if (imgs.length && imgs.every((img) => img.complete)) return;
    await wait(80);
  }
};

const captureMrcBillAsDataUrl = async (billData) => {
  if (!billData || typeof document === "undefined") return "";

  const host = document.createElement("div");
  host.style.cssText =
    "position:fixed;left:0;top:0;width:210mm;background:#fff;opacity:0.01;z-index:1;pointer-events:none;";
  document.body.appendChild(host);

  const billId = `mrc-efile-bill-${Date.now()}`;
  const root = createRoot(host);

  try {
    flushSync(() => {
      root.render(
        createElement(MrcElectricityConsumerBill, {
          id: billId,
          billData,
        })
      );
    });
    const billNode = document.getElementById(billId);
    if (!billNode) return "";

    await waitForImages(billNode);
    await wait(400);

    const canvas = await html2canvas(billNode, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
      width: billNode.scrollWidth,
      height: billNode.scrollHeight,
      onclone: (clonedDoc) => {
        const clonedBill = clonedDoc.getElementById(billId);
        if (!clonedBill) return;
        clonedBill.style.overflow = "visible";
        const logoColor =
          clonedBill.style.getPropertyValue("--bill-logo-color") || "#1b6b38";
        clonedBill.style.setProperty(
          "--bill-logo-color-light",
          getLogoColorLight(logoColor)
        );
      },
    });
    return canvas.toDataURL("image/jpeg", 0.92);
  } catch (e) {
    return "";
  } finally {
    try {
      root.unmount();
    } catch (e) {
      // ignore
    }
    if (host.parentNode) host.parentNode.removeChild(host);
  }
};

/**
 * When CP52 / bill already exist, build their pages for the MRC E-File.
 */
export const buildMrcEFileExtras = async ({
  application = {},
  documents = [],
  token,
  tariffs = [],
  includeCp52 = false,
  includeBill = false,
  companyCode = "15",
} = {}) => {
  const extras = { cp52Src: "", billSrc: "" };
  if ((!includeCp52 && !includeBill) || !application.applicationId || !token) {
    return extras;
  }

  const oracleTrackingId = extractMrcOracleTrackingId(
    application,
    application.oracleTrackingId,
    application.OracleTrackingId,
    application.billAdjTrackingId,
    application.billADJTrackingId,
    application.trackingId
  );
  if (!oracleTrackingId) return extras;

  let getResult = null;
  try {
    getResult = await fetchApprovedMrcBill(
      {
        applicationId: application.applicationId,
        oracleTrackingId,
        isPostedToIPS: false,
      },
      token
    );
  } catch (e) {
    return extras;
  }

  const subDivision =
    application.subDivisionCode ||
    application.subdivisionCode ||
    "";
  const resolvedCompanyCode =
    extractDiscoCodeFromSubDivisionCode(subDivision) ||
    String(companyCode || "15").slice(0, 2);
  const applicationPayable = parseApplicationPayable(application);
  const customerId =
    application.customerId || application.consumerId || "";

  const base = enrichCp52BaseDataFromDocuments(
    {
      subDivision,
      accountNo: customerId,
      consumerName:
        application.consumerName || application.applicantName || "",
      consumerAddress: application.consumerAddress || application.address || "",
      tariffCode: application.tariffCode || application.tariff || "",
      tariffDisplay: resolveMrcTariffDisplay(
        application.tariffCode || application.tariff || "",
        tariffs
      ),
      tariffs,
      billingMonth: formatCp52BillingMonth(application.billingMonth || ""),
      dueDate: application.dueDate || "",
      readingDate: application.readingDate || "",
      adjDate: application.dueDate || "",
      enqNo: application.applicationId || "",
      adjNumber: oracleTrackingId,
      customerId,
    },
    documents
  );
  const formData = mapMrcGetCp52ToFormData(getResult, base);

  if (includeCp52) {
    try {
      const cpDoc = createCPForm52OfMRCPdfDoc({
        companyCode: resolvedCompanyCode,
        officerName: "",
        designation: "",
        documents,
        data: {
          ...formData,
          tariffs,
          tariffDisplay:
            formData.tariffDisplay ||
            base.tariffDisplay ||
            "",
        },
      });
      extras.cp52Src = cpDoc.output("datauristring");
    } catch (e) {
      extras.cp52Src = "";
    }
  }

  if (includeBill) {
    try {
      const mapped = mapApprovedMrcBillResponse(getResult, {
        applicationId: application.applicationId,
        base: formData,
        documents,
        payableWithinDueDate: applicationPayable,
        customerId,
        tariffs,
      });
      if (mapped) {
        const cp52Row = unwrapMrcCp52Record(getResult) || {};
        const payableAmount = parseMrcBillNumber(
          mapped.payableWithinDueDate || mapped.grandTotal || 0
        );
        let raastQr = null;
        if (!mapped.hideQrContent && payableAmount >= 0) {
          try {
            const refNo = String(
              cp52Row.REFERENCE_NO || mapped.referenceNo || ""
            )
              .replace(/\s/g, "")
              .trim();
            raastQr = await fetchMrcBillPaymentQr({
              token,
              billNo: refNo,
              appNo: application.applicationId,
              refNo,
              amount:
                computeMrcBillQrAmount(
                  applicationPayable ||
                    mapped.applicationPayableWithinDueDate,
                  cp52Row.AMOUNT ?? cp52Row.CALCULATED_AMOUNT
                ) || Math.abs(payableAmount),
              dueDate: cp52Row.DUE_DT || mapped.dueDate || "",
              bPeriod:
                cp52Row.B_PERIOD ||
                cp52Row.COMPLAINTS_BILLING_MONTH ||
                mapped.billMonth ||
                "",
            });
          } catch (e) {
            raastQr = null;
          }
        }
        const mappedWithImages = await expandMrcBillMeterImages(mapped);
        extras.billSrc = await captureMrcBillAsDataUrl({
          ...mappedWithImages,
          raastQr,
        });
      }
    } catch (e) {
      extras.billSrc = "";
    }
  }

  return extras;
};

export default buildMrcEFileExtras;
